import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from '../components/ui/sonner';
import {
  Building2, RefreshCw, Lock, AlertTriangle, CheckCircle2, X, Pause, Ban, Sparkles, History, Loader2, Search,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  active:    { label: 'Active',    color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  trial:     { label: 'Trial',     color: 'bg-blue-100 text-blue-700 border-blue-200',           dot: 'bg-blue-500' },
  past_due:  { label: 'Past Due',  color: 'bg-amber-100 text-amber-800 border-amber-200',        dot: 'bg-amber-500' },
  suspended: { label: 'Suspended', color: 'bg-red-100 text-red-700 border-red-200',              dot: 'bg-red-500' },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-700 border-slate-200',        dot: 'bg-slate-400' },
};

const STATUS_OPTIONS = [
  { value: 'active',    label: 'Activate',   icon: CheckCircle2, color: 'text-emerald-700' },
  { value: 'trial',     label: 'Start trial', icon: Sparkles,    color: 'text-blue-700' },
  { value: 'past_due',  label: 'Mark past due', icon: AlertTriangle, color: 'text-amber-700' },
  { value: 'suspended', label: 'Suspend',    icon: Pause,        color: 'text-red-700' },
  { value: 'cancelled', label: 'Cancel',     icon: Ban,          color: 'text-slate-700' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.active;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold border ${m.color}`}
      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
};

const Subscriptions = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionTarget, setActionTarget] = useState(null); // { company, nextStatus }
  const [reason, setReason] = useState('');
  const [graceDays, setGraceDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [auditFor, setAuditFor] = useState(null); // company object
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/companies/subscriptions`, { withCredentials: true });
      setCompanies(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCompanies(); }, []);

  const openAudit = async (company) => {
    setAuditFor(company);
    setAudit([]);
    setAuditLoading(true);
    try {
      const r = await axios.get(`${API}/admin/companies/${company.id}/subscription-audit`, { withCredentials: true });
      setAudit(r.data || []);
    } catch (e) {
      toast.error('Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  };

  const applyStatus = async () => {
    if (!actionTarget) return;
    setBusy(true);
    try {
      await axios.put(
        `${API}/admin/companies/${actionTarget.company.id}/subscription-status`,
        {
          status: actionTarget.nextStatus,
          reason: reason || undefined,
          grace_period_days: actionTarget.nextStatus === 'past_due' ? Number(graceDays) : undefined,
        },
        { withCredentials: true },
      );
      toast.success(`Status set to ${actionTarget.nextStatus.replace('_', ' ')}`);
      setActionTarget(null);
      setReason('');
      setGraceDays(7);
      fetchCompanies();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update status');
    } finally {
      setBusy(false);
    }
  };

  const filtered = companies.filter((c) => {
    if (statusFilter !== 'all' && (c.subscription?.status || 'active') !== statusFilter) return false;
    if (query) {
      const q = query.toLowerCase();
      return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
    }
    return true;
  });

  const counts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s.value] = companies.filter((c) => (c.subscription?.status || 'active') === s.value).length;
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="subscriptions-admin-page">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-[#002FA7]/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#002FA7]" />
            </div>
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Subscriptions
              </h1>
              <p className="text-base text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Control every company's account access, billing status and grace periods.
              </p>
            </div>
          </div>
          <button
            onClick={fetchCompanies}
            data-testid="subs-refresh"
            className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-md text-sm text-slate-700 hover:bg-slate-50"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.value}
              data-testid={`filter-${s.value}`}
              onClick={() => setStatusFilter(statusFilter === s.value ? 'all' : s.value)}
              className={`text-left bg-white border rounded-md p-4 transition-all ${statusFilter === s.value ? 'border-[#002FA7] ring-1 ring-[#002FA7]' : 'border-slate-200 hover:border-slate-300'}`}
            >
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{STATUS_META[s.value]?.label}</p>
              <p className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>{counts[s.value] || 0}</p>
            </button>
          ))}
        </div>

        {/* Search + clear */}
        <div className="bg-white border border-slate-200 rounded-md mb-6">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              data-testid="subs-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by company name or email…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            />
            {statusFilter !== 'all' && (
              <button
                onClick={() => setStatusFilter('all')}
                data-testid="subs-clear-filter"
                className="text-xs text-[#002FA7] hover:underline flex items-center gap-1"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <X className="w-3 h-3" /> Clear filter
              </button>
            )}
          </div>

          {loading ? (
            <div className="px-6 py-10 flex items-center justify-center text-slate-500 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading companies…
            </div>
          ) : filtered.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              No companies match your filters.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Company</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Status</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Plan</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Next bill</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Grace</th>
                    <th className="text-right py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const s = c.subscription?.status || 'active';
                    return (
                      <tr key={c.id} data-testid={`company-row-${c.id}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="py-3 px-4">
                          <p className="font-semibold text-slate-900">{c.name || '—'}</p>
                          <p className="text-xs text-slate-500">{c.email || '—'}</p>
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge status={s} />
                          {c.subscription?.override_by_admin && (
                            <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">Manual override</p>
                          )}
                        </td>
                        <td className="py-3 px-4 text-slate-700">{c.subscription?.plan_name || '—'}</td>
                        <td className="py-3 px-4 text-slate-700">
                          {c.subscription?.next_billing_date ? fmtDate(c.subscription.next_billing_date) : '—'}
                          {s === 'past_due' && c.subscription?.suspend_at && (
                            <p className="text-[10px] text-amber-700 mt-0.5">Auto-suspend {fmtDate(c.subscription.suspend_at)}</p>
                          )}
                        </td>
                        <td className="py-3 px-4 text-slate-700">{c.subscription?.grace_period_days ?? 7}d</td>
                        <td className="py-3 px-4 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              data-testid={`audit-${c.id}`}
                              onClick={() => openAudit(c)}
                              className="px-2.5 py-1 rounded-md text-xs font-semibold text-slate-600 hover:bg-slate-100"
                              title="View history"
                            >
                              <History className="w-3.5 h-3.5 inline" /> History
                            </button>
                            {STATUS_OPTIONS.filter((opt) => opt.value !== s).map((opt) => {
                              const Icon = opt.icon;
                              return (
                                <button
                                  key={opt.value}
                                  data-testid={`set-${opt.value}-${c.id}`}
                                  onClick={() => { setActionTarget({ company: c, nextStatus: opt.value }); setReason(''); setGraceDays(c.subscription?.grace_period_days || 7); }}
                                  className={`px-2.5 py-1 rounded-md text-xs font-semibold hover:bg-slate-100 ${opt.color}`}
                                  title={opt.label}
                                >
                                  <Icon className="w-3.5 h-3.5 inline" /> {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Action confirm modal */}
      {actionTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50" onClick={() => !busy && setActionTarget(null)}>
          <div
            className="bg-white rounded-lg w-full max-w-md shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="subs-action-modal"
          >
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                {STATUS_OPTIONS.find((o) => o.value === actionTarget.nextStatus)?.label} — {actionTarget.company.name}
              </h2>
              <button onClick={() => !busy && setActionTarget(null)} data-testid="subs-action-cancel-x" className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Change <strong>{actionTarget.company.name}</strong> to <StatusBadge status={actionTarget.nextStatus} />.
                {actionTarget.nextStatus !== 'active' && ' All users in the company will be notified in-app.'}
              </p>
              {actionTarget.nextStatus === 'past_due' && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">
                    Grace period (days before auto-suspend)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={90}
                    value={graceDays}
                    onChange={(e) => setGraceDays(e.target.value)}
                    data-testid="subs-action-grace-days"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                  />
                </div>
              )}
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">
                  Reason (shown to company users)
                </label>
                <textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Payment received via bank transfer / Multiple failed invoices"
                  data-testid="subs-action-reason"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                />
              </div>
            </div>
            <div className="border-t border-slate-100 px-6 py-3 flex items-center justify-end gap-2">
              <button
                disabled={busy}
                onClick={() => setActionTarget(null)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md"
                data-testid="subs-action-cancel"
              >
                Cancel
              </button>
              <button
                disabled={busy}
                onClick={applyStatus}
                data-testid="subs-action-confirm"
                className="px-4 py-2 text-sm font-semibold text-white bg-[#002FA7] rounded-md hover:bg-[#00247D] disabled:opacity-50 flex items-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit modal */}
      {auditFor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50" onClick={() => setAuditFor(null)}>
          <div
            className="bg-white rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="subs-audit-modal"
          >
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Subscription history</h2>
                <p className="text-xs text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{auditFor.name}</p>
              </div>
              <button onClick={() => setAuditFor(null)} className="p-1 hover:bg-slate-100 rounded" data-testid="subs-audit-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto">
              {auditLoading ? (
                <div className="py-8 flex items-center justify-center text-slate-500 text-sm gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : audit.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No events yet.</p>
              ) : (
                <ol className="space-y-3 border-l-2 border-slate-200 pl-4" data-testid="subs-audit-list">
                  {audit.map((e) => (
                    <li key={e.id} data-testid={`subs-audit-item-${e.id}`} className="text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <div className="flex items-center gap-2 text-slate-900 font-semibold">
                        <StatusBadge status={e.from_status || 'active'} />
                        <span>→</span>
                        <StatusBadge status={e.to_status} />
                        <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-500">{e.source}</span>
                      </div>
                      {e.reason && <p className="text-xs text-slate-600 mt-1">{e.reason}</p>}
                      <p className="text-[11px] text-slate-500 mt-0.5">by {e.actor_name} {e.actor_role ? `(${e.actor_role})` : ''} • {fmtDateTime(e.created_at)}</p>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Subscriptions;
